import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * `<App />` — top-level Smart Health Account app shell.
 *
 * SCAFFOLD: minimal navigation across the eight first-class flow stub
 * views. Real app architecture (router, session-context provider,
 * auth-state-aware route gating, accessibility landmarks, etc.)
 * lands progressively across claim-queue items 2-9.
 *
 * v1 activation flow (claim-queue item 3) is wired in this PR via
 * the `activationFlow` prop. When omitted, the app constructs a
 * default mock-backed `ActivationFlow` for dev + tests; production
 * mounts inject the orchestrator with production-bound clients.
 */
import * as React from 'react';
import { ActivationFlow } from './activation/activation-flow.js';
import { AggregationElectionFlow, } from './aggregation/aggregation-flow.js';
import { MockConsentArtifactClient } from './api/consent-artifact-client.js';
import { MockIdentityProofingClient } from './api/identity-proofing-client.js';
import { MockPCIServiceClient, } from './api/pci-service-client.js';
import { MockProjectionServiceClient, } from './api/projection-service-client.js';
import { MockThirdPartyAppRegistryClient } from './api/third-party-app-registry-client.js';
import { AuthenticationFlow } from './auth/auth-flow.js';
import { ConsentFlow } from './consent/consent-flow.js';
import { EnvelopeCryptoAdapter, } from './crypto/envelope-crypto-adapter.js';
import { RecoveryFlow } from './recovery/recovery-flow.js';
import { createMockHolderRetainedErasure, ExportFlow, } from './export/export-flow.js';
import { LocaleProvider, useLocale } from './i18n/locale-context.js';
import { OnboardingFlow } from './onboarding/index.js';
import { InMemoryRenderingTaxonomyV0, } from './projection/rendering-taxonomy-client.js';
import { ActiveSession } from './session/active-session.js';
import { AccountHomeView, ActivationView, ActiveAuthorizationsView, AgentActivityView, AgentAuthorityConsentView, AggregationElectionView, AuthenticationView, InformedExportView, MockAgentHandleIssuanceClient, MultiLanguageAccessView, OnboardingFlowView, PatientProjectionView, PriorAuthView, RecoveryView, ThirdPartyConsentView, } from './views/index.js';
import { MockDemoOrchestratorClient, } from './api/demo-orchestrator-client.js';
// Lightning Strike v0.4 Path A canonical preservation forward — new app
// shell composition per CPL deposit 2026-05-11 §3.1+§3.2+§3.5. Legacy
// `<DemoGradePin />` + `<AppFooter />` source files retained at this
// PR-F1 additive step (dead code; not imported); PR-F3 deprecation
// cleanup deletes them per VPE-ratified Decoupled-then-integrate §2.11
// + M1 rollback safety canonical.
import { SandboxBanner } from './views/_components/sandbox-banner.js';
import { Footer } from './views/_components/footer.js';
const VIEW_NAV_LABEL_KEYS = {
    home: 'app.nav.home',
    activation: 'app.nav.activation',
    authentication: 'app.nav.authentication',
    aggregation: 'app.nav.aggregation',
    projection: 'app.nav.projection',
    recovery: 'app.nav.recovery',
    consent: 'app.nav.consent',
    language: 'app.nav.language',
    export: 'app.nav.export',
    'prior-auth': 'app.nav.prior_auth',
    'consent-agent': 'app.nav.consent_agent',
    'agent-activity': 'app.nav.agent_activity',
};
const VIEW_ORDER = [
    'home',
    'activation',
    'authentication',
    'aggregation',
    'projection',
    'recovery',
    'consent',
    'language',
    'export',
    'prior-auth',
    'consent-agent',
    'agent-activity',
];
const MOCK_SUBJECT_PCI = 'pci_mock_app_subject';
function defaultMockActivationFlow() {
    return new ActivationFlow({
        identityProofing: new MockIdentityProofingClient(),
        pciService: new MockPCIServiceClient(),
        consentArtifact: new MockConsentArtifactClient(),
        locale: 'en-US',
    });
}
/**
 * Build a default mock-backed `AuthenticationFlow` for dev + tests.
 *
 * Pre-seeds a wrapped KEK at the mock projection service so the
 * sign-in flow has material to fetch + unwrap. The seed
 * orchestration wraps a freshly-generated extractable KEK under a
 * synthetic password-derived PMK (matching what the patient would
 * input at sign-in). v0 dev experience: enter `dev-password` +
 * any non-empty verification code → sign-in succeeds end-to-end.
 *
 * Production mounts inject the orchestrator with production-bound
 * clients (no seeding here).
 *
 * Returns the flow plus its dependencies so the App shell can
 * thread the shared `ActiveSession` + `IProjectionServiceClient` +
 * `IEnvelopeCryptoAdapter` through to other views (notably
 * `<PatientProjectionView />`) that consume the post-sign-in
 * KEK-loaded session.
 */
async function buildDefaultMockAuthenticationFlow(activeSession, cryptoAdapter, projectionService) {
    const subjectPciHash = 'mock-subject-hash';
    const devPassword = 'dev-password';
    const salt = new Uint8Array(16);
    globalThis.crypto.getRandomValues(salt);
    const aad = new TextEncoder().encode(`${subjectPciHash}|kek-v1`);
    // Derive PMK from the dev password + salt (extractable so we can
    // wrap a KEK under it).
    const subtle = globalThis.crypto.subtle;
    const pwBytes = new TextEncoder().encode(devPassword);
    const pbkdf2Key = await subtle.importKey('raw', pwBytes, 'PBKDF2', false, [
        'deriveKey',
    ]);
    const pmkExtractable = await subtle.deriveKey({
        name: 'PBKDF2',
        salt: salt,
        iterations: 1000,
        hash: 'SHA-256',
    }, pbkdf2Key, { name: 'AES-GCM', length: 256 }, true, ['wrapKey', 'unwrapKey']);
    // Generate a fresh KEK + wrap under the PMK; ship the wrapped
    // ciphertext to the mock projection service.
    const kek = await cryptoAdapter.generateRandomKey({ extractable: true });
    const wrappedKek = await cryptoAdapter.wrapKey({
        outerKey: pmkExtractable,
        innerKey: kek,
        aad,
    });
    const stored = await projectionService.storeWrappedKek({
        subjectPciHash,
        wrappedKekBytes: wrappedKek,
        kekVersion: 1,
    });
    return new AuthenticationFlow({
        cryptoAdapter,
        projectionService,
        activeSession,
        inputContext: {
            subjectPciHash,
            pmkSalt: salt,
            kekId: stored.kekId,
            kekAad: aad,
        },
        // v0 stub possession factor: any non-empty code accepts.
        verifyPossessionFactor: async (factor) => factor.length > 0,
        // Match the seed's iteration count for dev-experience speed.
        pmkIterations: 1000,
    });
}
function defaultMockExportFlow() {
    return new ExportFlow({
        projectionService: new MockProjectionServiceClient(),
        subjectPciHash: 'mock-subject-hash',
        holderRetainedErasure: createMockHolderRetainedErasure(),
        mintAuthorizationToken: async () => 'mock-authorization-token',
    });
}
const sharedConsentArtifactClient = new MockConsentArtifactClient();
function defaultMockConsentFlow() {
    const registry = new MockThirdPartyAppRegistryClient();
    registry.seedApp({
        appIdentity: 'app_health_companion_v3',
        displayName: 'Health Companion',
        publisherName: 'Health Companion Inc.',
        tier: 'tier-1',
        status: 'active',
        registeredPurpose: 'Track your medications, surface refill reminders, and visualize lab trends.',
        trustCues: ['Admitted 2026-01-15', 'Last audited 2026-04-30'],
    });
    return new ConsentFlow({
        initiation: {
            appClaimedIdentity: 'app_health_companion_v3',
            requestedDataScope: ['clinical-encounters', 'lab-results', 'prescriptions'],
            requestedOperationalScope: ['read-only'],
            requestedTimeBounds: { kind: 'open-ended-until-revocation' },
            requestedRetentionDiscipline: 'aggregated-outputs-permitted',
            requestedPurpose: 'Track your medications, surface refill reminders, and visualize lab trends.',
            handbackUrl: 'https://example.invalid/health-companion/consent-return',
        },
        subjectPci: MOCK_SUBJECT_PCI,
        registry,
        consentArtifact: sharedConsentArtifactClient,
    });
}
export function App(props) {
    return (_jsx(LocaleProvider, { ...(props.initialLocale !== undefined ? { initialLocale: props.initialLocale } : {}), children: _jsx(AppShell, { ...(props.initialView !== undefined ? { initialView: props.initialView } : {}), ...(props.activationFlow !== undefined ? { activationFlow: props.activationFlow } : {}), ...(props.exportFlow !== undefined ? { exportFlow: props.exportFlow } : {}), ...(props.consentFlow !== undefined ? { consentFlow: props.consentFlow } : {}), ...(props.authenticationFlow !== undefined
                ? { authenticationFlow: props.authenticationFlow }
                : {}), ...(props.injectedSessionResidentDeps !== undefined
                ? { injectedSessionResidentDeps: props.injectedSessionResidentDeps }
                : {}), ...(props.demoOrchestratorClient !== undefined
                ? { demoOrchestratorClient: props.demoOrchestratorClient }
                : {}), ...(props.initialPriorAuthRunId !== undefined
                ? { initialPriorAuthRunId: props.initialPriorAuthRunId }
                : {}), ...(props.agentHandleIssuanceClient !== undefined
                ? { agentHandleIssuanceClient: props.agentHandleIssuanceClient }
                : {}), ...(props.initialAgentConsentRequest !== undefined
                ? { initialAgentConsentRequest: props.initialAgentConsentRequest }
                : {}) }) }));
}
function AppShell(props) {
    const locale = useLocale();
    const [view, setView] = React.useState(props.initialView ?? 'activation');
    const [activationFlow, setActivationFlow] = React.useState(() => props.activationFlow ?? defaultMockActivationFlow());
    const [exportFlow, setExportFlow] = React.useState(() => props.exportFlow ?? defaultMockExportFlow());
    const [consentFlow] = React.useState(() => props.consentFlow ?? defaultMockConsentFlow());
    // Phase 5 Phase D Patient PHG view — demo orchestrator client +
    // active run state. Per CPL Phase 5 Phase D IMPL composition guide
    // pair-with-DEV5 §3-§4 (2026-05-14): client is injected at production
    // mount, lazy-constructed `MockDemoOrchestratorClient` for dev/tests;
    // `initialPriorAuthRunId` seeds the run from `?runId=...` URL param
    // at production mount, else the empty-state launch CTA kicks off the
    // canonical knee-MRI scenario.
    const [demoOrchestratorClient] = React.useState(() => props.demoOrchestratorClient ?? new MockDemoOrchestratorClient());
    const [priorAuthRunId, setPriorAuthRunId] = React.useState(props.initialPriorAuthRunId);
    const handleLaunchPriorAuthDemo = React.useCallback(() => {
        void (async () => {
            const result = await demoOrchestratorClient.launchRun('knee-mri');
            setPriorAuthRunId(result.runId);
        })();
    }, [demoOrchestratorClient]);
    // Mode C agent authority delegation consent flow state per CPL Mode C
    // consent flow v0.1 (`CSO/Product/cpl-mode-c-agent-authority-delegation-
    // consent-flow-v0.1.md`, 2026-05-16). Substrate-side handle issuance
    // composes with DEV6 sandbox-auth-backend `/v0/agent-handle/issue` at
    // production; dev/test mounts use the mock client.
    const [agentHandleIssuanceClient] = React.useState(() => props.agentHandleIssuanceClient ?? new MockAgentHandleIssuanceClient());
    const [grantedAgentHandle, setGrantedAgentHandle] = React.useState(undefined);
    const [agentRevoked, setAgentRevoked] = React.useState(false);
    const [agentActivityEntries] = React.useState([]);
    const handleAgentRevoke = React.useCallback(() => {
        // v0 stub — sandbox-tier revocation transitions the indicator to
        // `inactive` immediately. Production-tier wires through the
        // substrate revocation primitive (≤60s propagation per PBoR right #5).
        setAgentRevoked(true);
    }, []);
    // Session-resident deps — constructed once + stable across
    // re-renders. The auth flow consumes these; `<PatientProjectionView />`
    // + `<AggregationElectionView />` (post-sign-in) consume these;
    // `<RecoveryView />` consumes these regardless of auth state.
    // Production mounts + Surface 1 demo wrapper inject pre-built deps
    // via `App.injectedSessionResidentDeps`; dev/test mounts construct
    // mocks lazily in the auth-flow setup effect below so the same
    // instances thread through.
    const [sessionDeps, setSessionDeps] = React.useState(() => props.injectedSessionResidentDeps ?? null);
    // Auth flow + active session: lazy-built (async) when not
    // injected. Until built, the auth view shows a "preparing"
    // placeholder; once built, it renders the real flow. When deps are
    // injected without an auth flow, the lazy-build still runs; callers
    // injecting deps SHOULD also inject `authenticationFlow` to avoid
    // double-construction (see `AppProps.injectedSessionResidentDeps`
    // JSDoc).
    const [authenticationFlow, setAuthenticationFlow] = React.useState(() => props.authenticationFlow ?? null);
    React.useEffect(() => {
        if (props.authenticationFlow !== undefined || authenticationFlow !== null) {
            return;
        }
        let cancelled = false;
        void (async () => {
            // Default mock-build threads a designated demo `tenantId` per
            // the canonical AAD field set (`encodeRecoveryAad` non-empty
            // tenantId validation). Production mounts inject `authenticationFlow`
            // with the auth-flow-resolved tenant binding.
            const session = new ActiveSession({
                subject: {
                    subjectPciHash: 'mock-subject-hash',
                    tenantId: 'demo-tenant',
                },
            });
            const adapter = new EnvelopeCryptoAdapter();
            const projection = new MockProjectionServiceClient();
            // UC1 sub-fire #7: seed sample data for M1 Lightning Strike demo
            // surface (Records / Activity tabs at <AccountHomeView />). Idempotent;
            // production session bootstrap skips seed (real substrate-side data
            // wires post substrate-state-event firing per Smart ID Layer B
            // substrate-state-event-paced).
            seedSampleAccountHomeData(projection, session.subject.subjectPciHash);
            const flow = await buildDefaultMockAuthenticationFlow(session, adapter, projection);
            if (!cancelled) {
                setSessionDeps({
                    activeSession: session,
                    cryptoAdapter: adapter,
                    projectionService: projection,
                    taxonomy: new InMemoryRenderingTaxonomyV0(),
                });
                setAuthenticationFlow(flow);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [props.authenticationFlow, authenticationFlow]);
    // Track auth-flow state to know when KEK is loaded into the
    // active session — that's the point at which post-sign-in views
    // (patient projection, etc.) can wire their flow with real
    // session-resident deps. Before sign-in, downstream views render
    // their unwired scaffold (existing behavior preserved).
    const [authFlowState, setAuthFlowState] = React.useState(null);
    React.useEffect(() => {
        if (authenticationFlow === null) {
            return;
        }
        setAuthFlowState(authenticationFlow.getState().kind);
        return authenticationFlow.subscribe((s) => setAuthFlowState(s.kind));
    }, [authenticationFlow]);
    const patientProjectionFlow = React.useMemo(() => {
        if (sessionDeps === null)
            return undefined;
        if (authFlowState !== 'session-active')
            return undefined;
        return {
            session: sessionDeps.activeSession,
            client: sessionDeps.projectionService,
            adapter: sessionDeps.cryptoAdapter,
            taxonomy: sessionDeps.taxonomy,
            kekVersion: 1,
        };
    }, [sessionDeps, authFlowState]);
    // Recovery flow — construct once sessionDeps are available. Gate is
    // sessionDeps construction (not `authFlowState === 'session-active'`)
    // because recovery is the flow the patient runs when they CANNOT
    // sign in. Subject identity is read from the active session so
    // production mounts (which inject the auth flow with the real
    // subject) compose without further wiring.
    const [recoveryFlow, setRecoveryFlow] = React.useState(null);
    React.useEffect(() => {
        if (sessionDeps === null)
            return;
        if (recoveryFlow !== null)
            return;
        setRecoveryFlow(new RecoveryFlow({
            // Thread `tenantId` from the active session subject per
            // `recovery_flow_phase_b_step_3b_design.md` v0.4 §3.1 Step 3
            // canonical AAD field set. Production session bootstrap MUST
            // populate `subject.tenantId`; if undefined at recovery-time,
            // the §3.1 cycle surfaces as `'unrecoverable'` per Doctrine
            // v1.3 §8 B.3 ELEVATE non-discriminating discipline.
            subject: {
                subjectPciHash: sessionDeps.activeSession.subject.subjectPciHash,
                ...(sessionDeps.activeSession.subject.tenantId !== undefined
                    ? { tenantId: sessionDeps.activeSession.subject.tenantId }
                    : {}),
            },
            client: sessionDeps.projectionService,
            adapter: sessionDeps.cryptoAdapter,
            session: sessionDeps.activeSession,
        }));
    }, [sessionDeps, recoveryFlow]);
    // Aggregation election flow — gate on session-active (matches
    // patientProjectionFlow gating; aggregation election presumes a
    // signed-in patient making post-sign-in fiduciary decisions).
    const [aggregationElectionFlow, setAggregationElectionFlow] = React.useState(null);
    React.useEffect(() => {
        if (sessionDeps === null)
            return;
        if (authFlowState !== 'session-active')
            return;
        if (aggregationElectionFlow !== null)
            return;
        setAggregationElectionFlow(new AggregationElectionFlow({
            subjectPciHash: sessionDeps.activeSession.subject.subjectPciHash,
            client: sessionDeps.projectionService,
        }));
    }, [sessionDeps, authFlowState, aggregationElectionFlow]);
    // SHA v0 patient onboarding flow — gate on activationFlow +
    // authenticationFlow both being available. The orchestrator
    // subscribes to both upstream flows; activationFlow is constructed
    // synchronously at AppShell mount but authenticationFlow is
    // lazy-built (async). Once both are present, construct
    // OnboardingFlow once + dispose on replacement / unmount to release
    // upstream-flow subscriptions per `OnboardingFlow.dispose()`.
    //
    // Per `sha_v0_patient_onboarding_flow_design.md` §3.4 + handoff
    // PR #440 §"Items remaining" item 6: 'home' route renders
    // <OnboardingFlowView /> while onboarding is in-flight; on
    // 'account-home-landing' (or terminal cancelled/error), renders
    // <AccountHomeView />. Pre-onboarding-flow-construction (auth flow
    // not yet lazy-built), 'home' falls through to <AccountHomeView />
    // placeholder body — preserves existing pre-session-active behavior.
    //
    // Injection-seam carve-out: when authenticationFlow is observed
    // already at 'session-active' (Surface 1 demo wrapper / production
    // mounts that compose pre-onboarded sessions), skip onboarding-flow
    // construction — the patient is past first-session bootstrap; home
    // route falls through to <AccountHomeView /> directly per existing
    // injection-seam semantics.
    const [onboardingFlow, setOnboardingFlow] = React.useState(null);
    React.useEffect(() => {
        if (authenticationFlow === null)
            return;
        if (onboardingFlow !== null)
            return;
        if (authenticationFlow.getState().kind === 'session-active')
            return;
        const flow = new OnboardingFlow({
            activationFlow,
            authenticationFlow,
        });
        setOnboardingFlow(flow);
        return () => {
            flow.dispose();
        };
    }, [activationFlow, authenticationFlow, onboardingFlow]);
    const [onboardingState, setOnboardingState] = React.useState(null);
    React.useEffect(() => {
        if (onboardingFlow === null)
            return;
        return onboardingFlow.subscribe(setOnboardingState);
    }, [onboardingFlow]);
    const handleRestartActivation = React.useCallback(() => {
        setActivationFlow(defaultMockActivationFlow());
    }, []);
    const handleRestartExport = React.useCallback(() => {
        setExportFlow(defaultMockExportFlow());
    }, []);
    const handleRestartRecovery = React.useCallback(() => {
        if (sessionDeps === null)
            return;
        setRecoveryFlow(new RecoveryFlow({
            // Thread `tenantId` from the active session subject per
            // `recovery_flow_phase_b_step_3b_design.md` v0.4 §3.1 Step 3
            // canonical AAD field set. Production session bootstrap MUST
            // populate `subject.tenantId`; if undefined at recovery-time,
            // the §3.1 cycle surfaces as `'unrecoverable'` per Doctrine
            // v1.3 §8 B.3 ELEVATE non-discriminating discipline.
            subject: {
                subjectPciHash: sessionDeps.activeSession.subject.subjectPciHash,
                ...(sessionDeps.activeSession.subject.tenantId !== undefined
                    ? { tenantId: sessionDeps.activeSession.subject.tenantId }
                    : {}),
            },
            client: sessionDeps.projectionService,
            adapter: sessionDeps.cryptoAdapter,
            session: sessionDeps.activeSession,
        }));
    }, [sessionDeps]);
    const handleRestartAggregation = React.useCallback(() => {
        if (sessionDeps === null)
            return;
        if (authFlowState !== 'session-active')
            return;
        setAggregationElectionFlow(new AggregationElectionFlow({
            subjectPciHash: sessionDeps.activeSession.subject.subjectPciHash,
            client: sessionDeps.projectionService,
        }));
    }, [sessionDeps, authFlowState]);
    return (_jsxs("div", { className: "shn-app-shell", children: [_jsx(SandboxBanner, {}), _jsx("header", { className: "shn-app-header", children: _jsx("h1", { id: "app-heading", className: "shn-app-heading", children: locale.t('app.title') }) }), _jsxs("main", { className: "shn-app-main", "aria-labelledby": "app-heading", children: [_jsx("nav", { className: "shn-sidebar-nav", "aria-label": locale.t('app.nav.aria_label'), children: _jsx("ul", { children: VIEW_ORDER.map((key) => (_jsx("li", { children: _jsx("button", { type: "button", "aria-current": view === key ? 'page' : undefined, onClick: () => setView(key), children: locale.t(VIEW_NAV_LABEL_KEYS[key]) }) }, key))) }) }), _jsx("div", { className: "shn-app-content", children: renderView(view, {
                            activationFlow,
                            exportFlow,
                            consentFlow,
                            authenticationFlow,
                            patientProjectionFlow,
                            recoveryFlow,
                            aggregationElectionFlow,
                            onboardingFlow,
                            onboardingState,
                            activeSession: sessionDeps?.activeSession ?? null,
                            projectionService: sessionDeps?.projectionService ?? null,
                            demoOrchestratorClient,
                            priorAuthRunId,
                            onLaunchPriorAuthDemo: handleLaunchPriorAuthDemo,
                            agentHandleIssuanceClient,
                            initialAgentConsentRequest: props.initialAgentConsentRequest,
                            grantedAgentHandle,
                            agentActivityEntries,
                            agentRevoked,
                            onAgentGranted: setGrantedAgentHandle,
                            onAgentRevoke: handleAgentRevoke,
                            onRestartActivation: handleRestartActivation,
                            onRestartExport: handleRestartExport,
                            onRestartRecovery: handleRestartRecovery,
                            onRestartAggregation: handleRestartAggregation,
                        }) })] }), _jsx(Footer, {})] }));
}
/**
 * Sample Care tab data per CPL Smart Health Account dashboard UX v0.2
 * §3.4 wireframe for M1 Lightning Strike sandbox-grade demo surface.
 * UC1 Patient Access Sprint 9 sub-fire #7 per VPE explicit GO directive
 * 2026-05-11 (substrate-side enabler for MSE Vercel deployment front-end
 * visibility at June 9 RHTP Summit M1).
 */
const SAMPLE_CARE_APPOINTMENTS = [
    {
        appointmentId: 'appt_demo_1',
        when: 'May 15, 2026 · 2:30 PM',
        location: 'Memorial Hospital',
        description: 'MRI Lumbar Spine',
        providerName: 'Dr. Wilson',
        federatedPriorAuthStatus: 'approved',
    },
    {
        appointmentId: 'appt_demo_2',
        when: 'May 22, 2026 · 10:00 AM',
        location: 'One Medical',
        description: 'Annual physical',
        providerName: 'Dr. Smith',
        federatedPriorAuthStatus: 'not-required',
    },
];
const SAMPLE_CARE_TASKS = [
    {
        taskId: 'task_demo_1',
        description: 'Confirm prior auth approval with Memorial Hospital',
        completed: false,
    },
    {
        taskId: 'task_demo_2',
        description: 'Review imaging report after May 15 encounter',
        completed: false,
    },
];
/**
 * Seed sample data into a fresh `MockProjectionServiceClient` instance for
 * the M1 demo surface (sub-fire #7). Composes with sample Records tab data
 * (projection records) + sample Activity tab data (patient ops log entries
 * with provenance attestations). Idempotent — only seeds when projection
 * records map is empty (prevents double-seed on hot reload).
 */
function seedSampleAccountHomeData(projectionService, subjectPciHash) {
    // Records tab — 3 sample projection records spanning content categories.
    projectionService.seedProjectionRecord({
        recordId: 'rec_demo_consent_1',
        sourceAuditRecordId: 'audit_demo_1',
        subjectPciHash,
        contentCategory: 'consent-artifact',
        contentCiphertext: new Uint8Array([1, 2, 3]),
        dekId: 'dek_demo_1',
        dekCiphertext: new Uint8Array([4, 5, 6]),
        kekId: 'kek_demo_1',
        projectionAnchoredAt: '2026-05-10T15:30:00.000Z',
    });
    projectionService.seedProjectionRecord({
        recordId: 'rec_demo_aggregation_1',
        sourceAuditRecordId: 'audit_demo_2',
        subjectPciHash,
        contentCategory: 'aggregated-payload',
        contentCiphertext: new Uint8Array([7, 8, 9]),
        dekId: 'dek_demo_2',
        dekCiphertext: new Uint8Array([10, 11, 12]),
        kekId: 'kek_demo_2',
        projectionAnchoredAt: '2026-05-09T10:00:00.000Z',
    });
    projectionService.seedProjectionRecord({
        recordId: 'rec_demo_operation_1',
        sourceAuditRecordId: 'audit_demo_3',
        subjectPciHash,
        contentCategory: 'operation-record',
        contentCiphertext: new Uint8Array([13, 14, 15]),
        dekId: 'dek_demo_3',
        dekCiphertext: new Uint8Array([16, 17, 18]),
        kekId: 'kek_demo_3',
        projectionAnchoredAt: '2026-05-08T09:15:00.000Z',
    });
    // Activity tab — sample patient ops log entries keyed by subjectPciId.
    // At v0 sandbox-grade we use subjectPciHash as the subjectPciId proxy
    // (production session bootstrap canonicalizes subjectPciId distribution
    // per Smart ID Layer B substrate-state-event-paced).
    projectionService.seedPatientOpsLog(subjectPciHash, [
        {
            recordId: 'opslog_demo_1',
            operationType: 'consent.granted',
            recordedAt: '2026-05-10T15:30:00.000Z',
            initiatedAt: '2026-05-10T15:29:55.000Z',
            initiatorHolderName: 'Health Companion',
            targetHolders: ['app_health_companion_v3'],
            authorizationOutcome: 'authorized',
        },
        {
            recordId: 'opslog_demo_2',
            operationType: 'projection.read',
            recordedAt: '2026-05-09T11:00:00.000Z',
            initiatedAt: '2026-05-09T10:59:50.000Z',
            initiatorHolderName: 'Memorial Hospital',
            targetHolders: ['provider_memorial_hospital'],
            authorizationOutcome: 'authorized',
        },
    ]);
}
function renderView(view, ctx) {
    switch (view) {
        case 'home': {
            // Onboarding-vs-home dispatch per `sha_v0_patient_onboarding_flow_design.md`
            // §3.4 + handoff PR #440 §"Items remaining" item 6.
            //
            // Pre-onboarding-flow-construction (auth-flow not yet lazy-built):
            //   falls through to <AccountHomeView /> placeholder body —
            //   preserves existing pre-session-active smoke-test contract.
            // Onboarding in-flight (state.kind not 'account-home-landing' /
            //   'cancelled' / 'error'): renders <OnboardingFlowView />.
            // Onboarding terminal at 'account-home-landing' (or 'cancelled'/'error'
            //   to avoid stranding the patient on a terminal onboarding view):
            //   renders <AccountHomeView />.
            const onboardingComplete = ctx.onboardingState === null ||
                ctx.onboardingState.kind === 'account-home-landing' ||
                ctx.onboardingState.kind === 'cancelled' ||
                ctx.onboardingState.kind === 'error';
            if (!onboardingComplete && ctx.onboardingFlow !== null) {
                return _jsx(OnboardingFlowView, { flow: ctx.onboardingFlow });
            }
            // UC1 sub-fire #7: M1 Lightning Strike demo surface wiring. When
            // session + projectionService are available (post-onboarding), pass
            // integration props for Records / Activity / Care / Settings tabs.
            // Healthcare team tab remains placeholder (substrate-state-event-paced
            // on ADR-033 substrate-fiduciary-managed handle multi-role primitive
            // Sprint 9+ chained per VPE direction 2026-05-11 named-trigger wait).
            const accountHomeIntegrationReady = ctx.activeSession !== null && ctx.projectionService !== null;
            return (_jsx(AccountHomeView, { ...(ctx.activeSession !== null ? { session: ctx.activeSession } : {}), ...(accountHomeIntegrationReady
                    ? {
                        recordsListIntegration: {
                            client: ctx.projectionService,
                            subjectPciHash: ctx.activeSession.subject.subjectPciHash,
                            maxRender: 10,
                            ...(ctx.activeSession.subject.tenantId !== undefined
                                ? { tenantId: ctx.activeSession.subject.tenantId }
                                : {}),
                        },
                        opsLogIntegration: {
                            client: ctx.projectionService,
                            // v0 sandbox-grade: subjectPciHash used as subjectPciId proxy;
                            // production canonicalizes subjectPciId distribution per
                            // Smart ID Layer B substrate-state-event-paced.
                            subjectPciId: ctx.activeSession.subject.subjectPciHash,
                            trustedAttestationKeys: new Map(),
                            ...(ctx.activeSession.subject.tenantId !== undefined
                                ? { tenantId: ctx.activeSession.subject.tenantId }
                                : {}),
                        },
                        careIntegration: {
                            appointments: SAMPLE_CARE_APPOINTMENTS,
                            tasks: SAMPLE_CARE_TASKS,
                        },
                        onSettingsCTA: (kind) => {
                            // eslint-disable-next-line no-console
                            console.log(`[app.tsx UC1 sub-fire #7] Settings CTA fired: ${kind} (substrate-state-event-paced for real routing)`);
                        },
                    }
                    : {}) }));
        }
        case 'activation':
            return (_jsx(ActivationView, { flow: ctx.activationFlow, onRestartRequested: ctx.onRestartActivation }));
        case 'authentication':
            if (ctx.authenticationFlow === null) {
                return (_jsxs("section", { "aria-labelledby": "auth-preparing-heading", "aria-busy": "true", children: [_jsx("h2", { id: "auth-preparing-heading", children: "Preparing sign-in" }), _jsx("p", { role: "status", children: "Loading authentication infrastructure..." })] }));
            }
            return _jsx(AuthenticationView, { flow: ctx.authenticationFlow });
        case 'aggregation':
            return (_jsx(AggregationElectionView, { ...(ctx.aggregationElectionFlow !== null
                    ? {
                        flow: ctx.aggregationElectionFlow,
                        onRestartRequested: ctx.onRestartAggregation,
                    }
                    : {}) }));
        case 'projection':
            return (_jsx(PatientProjectionView, { ...(ctx.patientProjectionFlow !== undefined
                    ? { flow: ctx.patientProjectionFlow }
                    : {}) }));
        case 'recovery':
            return (_jsx(RecoveryView, { ...(ctx.recoveryFlow !== null
                    ? {
                        flow: ctx.recoveryFlow,
                        onRestartRequested: ctx.onRestartRecovery,
                    }
                    : {}) }));
        case 'consent':
            return (_jsxs(_Fragment, { children: [_jsx(ThirdPartyConsentView, { flow: ctx.consentFlow }), _jsx(ActiveAuthorizationsView, { subjectPci: MOCK_SUBJECT_PCI, consentArtifact: sharedConsentArtifactClient })] }));
        case 'language':
            return _jsx(MultiLanguageAccessView, {});
        case 'export':
            return (_jsx(InformedExportView, { flow: ctx.exportFlow, onRestartRequested: ctx.onRestartExport }));
        case 'prior-auth': {
            // Phase 5 Phase D Patient PHG view per CPL Phase 5 Phase D IMPL
            // composition guide pair-with-DEV5 §3 (2026-05-14). When session +
            // projectionService are available (post-onboarding), pass the
            // audit-chain integration so `<OpsLogContainer />` renders below
            // the tracker. The launch CTA fires `demoOrchestratorClient.launchRun`
            // which sets `priorAuthRunId` in AppShell state.
            const auditChainReady = ctx.activeSession !== null && ctx.projectionService !== null;
            return (_jsx(PriorAuthView, { ...(ctx.activeSession !== null ? { session: ctx.activeSession } : {}), demoOrchestratorClient: ctx.demoOrchestratorClient, ...(ctx.priorAuthRunId !== undefined
                    ? { runId: ctx.priorAuthRunId }
                    : {}), onLaunchDemo: ctx.onLaunchPriorAuthDemo, ...(auditChainReady
                    ? {
                        auditChainIntegration: {
                            client: ctx.projectionService,
                            subjectPciId: ctx.activeSession.subject.subjectPciHash,
                            trustedAttestationKeys: new Map(),
                            ...(ctx.activeSession.subject.tenantId !== undefined
                                ? { tenantId: ctx.activeSession.subject.tenantId }
                                : {}),
                        },
                    }
                    : {}) }));
        }
        case 'consent-agent': {
            // Mode C agent authority delegation consent flow per CPL Mode C
            // consent flow v0.1 §2.2–§2.4 (2026-05-16). At production mount
            // the OAuth-style handback URL query params populate
            // `initialAgentConsentRequest`; sandbox-tier renders a synthetic
            // request so the view is exercisable in dev without a partner
            // app bouncing into it.
            const req = ctx.initialAgentConsentRequest ?? {
                requestId: 'sandbox_request_001',
                agentId: 'sandbox-agent',
                agentName: 'Sandbox AI Agent',
                scopeSummary: 'Read your authorized health records to summarize medications, allergies, and recent encounters.',
                durationLabel: '30 days',
                returnUrl: undefined,
            };
            return (_jsx(AgentAuthorityConsentView, { agentId: req.agentId, ...(req.agentName !== undefined ? { agentName: req.agentName } : {}), requestId: req.requestId, scopeSummary: req.scopeSummary ?? 'Access scope per Use Authority template.', durationLabel: req.durationLabel ?? 'Until manually revoked', ...(req.returnUrl !== undefined ? { returnUrl: req.returnUrl } : {}), handleIssuanceClient: ctx.agentHandleIssuanceClient, onGranted: ctx.onAgentGranted }));
        }
        case 'agent-activity': {
            // Mode C Stage (5) post-grant agent activity tracker per CPL
            // Mode C v0.1 §2.5. Composes with `<OpsLogContainer />` (audit
            // chain) + `<MultiPartySignatureChipGroup />` (Four-Body Role
            // Definitions v1.0 attestations). When no grant has fired yet,
            // the view renders the no-activity state with `agentName` from
            // the synthetic sandbox request.
            const req = ctx.initialAgentConsentRequest ?? {
                requestId: 'sandbox_request_001',
                agentId: 'sandbox-agent',
                agentName: 'Sandbox AI Agent',
                scopeSummary: 'Access scope per Use Authority template.',
                durationLabel: '30 days',
                returnUrl: undefined,
            };
            const auditChainReady = ctx.activeSession !== null && ctx.projectionService !== null;
            return (_jsx(AgentActivityView, { agentId: req.agentId, ...(req.agentName !== undefined ? { agentName: req.agentName } : {}), ...(ctx.grantedAgentHandle !== undefined
                    ? {
                        handleId: ctx.grantedAgentHandle.handleId,
                        grantedAt: ctx.grantedAgentHandle.issuedAt,
                    }
                    : { grantedAt: '—' }), scopeSummary: req.scopeSummary ?? 'Access scope per Use Authority template.', durationLabel: req.durationLabel ?? 'Until manually revoked', activityEntries: ctx.agentActivityEntries, isRevoked: ctx.agentRevoked, onRevoke: ctx.onAgentRevoke, ...(auditChainReady
                    ? {
                        auditChainIntegration: {
                            client: ctx.projectionService,
                            subjectPciId: ctx.activeSession.subject.subjectPciHash,
                            trustedAttestationKeys: new Map(),
                            ...(ctx.activeSession.subject.tenantId !== undefined
                                ? { tenantId: ctx.activeSession.subject.tenantId }
                                : {}),
                        },
                    }
                    : {}) }));
        }
    }
}
//# sourceMappingURL=app.js.map